import React, { useEffect, useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  Package,
  X,
  Zap,
  DollarSign,
  Heart,
  MessageCircle,
  Share2,
  Eye,
  BarChart3,
  Users,
  Instagram,
  Facebook,
  Twitter,
  Youtube,
  Linkedin,
  Calendar,
  Building2,
  ExternalLink,
  Edit,
  TrendingUp,
  Hash,
  FileText,
  ChevronDown,
  Filter,
  Search,
  Info
} from 'lucide-react'
import TikTokIcon from '../ui/TikTokIcon'
import { Card, Tooltip } from '../ui'
import useAssetStore from '../../stores/assetStore'
import { Asset, AssetPerformanceMetrics } from '../../types/asset'
import { assetService } from '../../services/assetService'
import { getPartnerLogoUrl } from '../../services/partnerService'
import { useToast } from '../../contexts/ToastContext'

interface AssetDetailDrawerProps {
  isOpen: boolean
  onClose: () => void
  asset: Asset | null
  onEditClick?: () => void
}

// Get platform icon component
const getPlatformIcon = (platformId: number) => {
  switch (platformId) {
    case 1:
      return Instagram
    case 2:
      return TikTokIcon
    case 3:
      return Facebook
    case 4:
      return Twitter
    case 5:
      return Youtube
    case 6:
      return Linkedin
    default:
      return Instagram
  }
}

// Get platform color
const getPlatformColor = (platformId: number) => {
  switch (platformId) {
    case 1:
      return 'text-pink-500'
    case 2:
      return 'text-black'
    case 3:
      return 'text-blue-600'
    case 4:
      return 'text-blue-400'
    case 5:
      return 'text-red-600'
    case 6:
      return 'text-blue-700'
    default:
      return 'text-gray-500'
  }
}

// Format large numbers with K, M suffixes
const formatNumber = (num: number): string => {
  if (num >= 1000000) {
    return `${(num / 1000000).toFixed(1)}M`
  }
  if (num >= 1000) {
    return `${(num / 1000).toFixed(1)}K`
  }
  return num.toLocaleString()
}

// Format currency
const formatCurrency = (num: number): string => {
  return num.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })
}

const formatDate = (dateString: string | null) => {
  if (!dateString) return '-'
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  })
}

const truncateCaption = (caption: string | null, maxLength: number = 150) => {
  if (!caption) return ''
  if (caption.length <= maxLength) return caption
  return caption.substring(0, maxLength) + '...'
}

interface AssetActivation {
  id: string
  name: string
  description: string | null
  partner_id: string
  partner_name: string | null
  partner_logo_url: string | null
  activation_type_id: string | null
  activation_type_name: string | null
  deal_value: number | null
  emv_target_value: number | null
  start_date: string | null
  end_date: string | null
  status: string
  created_at: string | null
}

interface AssetPost {
  partition_key: number
  post_id: number
  post_url: string
  posted_at: string | null
  caption: string | null
  platform_id: number
  username: string | null
  emv: number
  views: number
  likes: number
  comments: number
  shares: number
  saves: number
  engagement: number
  hashtags: string[]
}

const AssetDetailDrawer: React.FC<AssetDetailDrawerProps> = ({
  isOpen,
  onClose,
  asset,
  onEditClick
}) => {
  const { loadAsset, selectedAsset, loadAssetPerformance } = useAssetStore()
  const { showError } = useToast()

  const [activeTab, setActiveTab] = useState<'overview' | 'activations' | 'posts'>('overview')
  const [activations, setActivations] = useState<AssetActivation[]>([])
  const [posts, setPosts] = useState<AssetPost[]>([])
  const [activationsLoading, setActivationsLoading] = useState(false)
  const [postsLoading, setPostsLoading] = useState(false)
  const [performanceData, setPerformanceData] = useState<any>(null)
  const [performanceLoading, setPerformanceLoading] = useState(false)

  // Expandable activation state
  const [expandedActivationId, setExpandedActivationId] = useState<string | null>(null)
  const [activationPerformanceCache, setActivationPerformanceCache] = useState<
    Record<string, AssetPerformanceMetrics>
  >({})
  const [activationPerfLoading, setActivationPerfLoading] = useState<string | null>(null)

  // Activation filter state
  const [activationStatusFilter, setActivationStatusFilter] = useState<string>('all')

  // Posts search state
  const [postsSearch, setPostsSearch] = useState('')

  // Expansion state for breakdown sections
  const [expandedSections, setExpandedSections] = useState<{
    accounts: boolean;
    hashtags: boolean;
    formats: boolean;
  }>({ accounts: false, hashtags: false, formats: false })

  // Status priority for sorting (Active first)
  const statusPriority: Record<string, number> = {
    'Active': 0,
    'Pending': 1,
    'Suspended': 2,
    'Completed': 3,
  }

  // Filter and sort activations
  const filteredActivations = activations
    .filter(a => activationStatusFilter === 'all' || a.status === activationStatusFilter)
    .sort((a, b) => {
      // First sort by status priority
      const statusA = statusPriority[a.status] ?? 99
      const statusB = statusPriority[b.status] ?? 99
      if (statusA !== statusB) return statusA - statusB
      // Then sort by EMV target descending
      return (b.emv_target_value || b.deal_value || 0) - (a.emv_target_value || a.deal_value || 0)
    })

  // Filter and sort posts - by EMV descending, with search
  const filteredPosts = posts
    .filter(p => {
      if (!postsSearch.trim()) return true
      const searchLower = postsSearch.toLowerCase()
      return (
        (p.username && p.username.toLowerCase().includes(searchLower)) ||
        (p.caption && p.caption.toLowerCase().includes(searchLower)) ||
        (p.hashtags && p.hashtags.some(h => h.toLowerCase().includes(searchLower)))
      )
    })
    .sort((a, b) => b.emv - a.emv)

  // Reset state when drawer closes
  useEffect(() => {
    if (!isOpen) {
      setPerformanceData(null)
      setActivations([])
      setPosts([])
      setActiveTab('overview')
      setExpandedActivationId(null)
      setActivationPerformanceCache({})
      setActivationPerfLoading(null)
      setActivationStatusFilter('all')
      setPostsSearch('')
      setExpandedSections({ accounts: false, hashtags: false, formats: false })
    }
  }, [isOpen])

  // Load full asset details when drawer opens
  useEffect(() => {
    if (isOpen && asset) {
      loadAsset(asset.id, asset.customer_id)
      setActiveTab('overview')

      // Load performance data from API
      setPerformanceLoading(true)
      loadAssetPerformance(asset.id, asset.customer_id)
        .then((apiData) => {
          if (apiData) {
            setPerformanceData(apiData)
          } else {
            // API returned null - show error
            showError('Failed to Load', 'Could not load asset performance data')
            setPerformanceData({
              totalPosts: 0,
              totalEmv: 0,
              totalLikes: 0,
              totalComments: 0,
              totalShares: 0,
              totalViews: 0,
              totalSaves: 0,
              totalEngagement: 0,
              accountBreakdown: [],
              hashtagBreakdown: []
            })
          }
        })
        .catch((err) => {
          console.error('[AssetDetailDrawer] Failed to load performance:', err)
          showError('Server Error', 'Could not connect to the server')
          setPerformanceData({
            totalPosts: 0,
            totalEmv: 0,
            totalLikes: 0,
            totalComments: 0,
            totalShares: 0,
            totalViews: 0,
            totalSaves: 0,
            totalEngagement: 0,
            accountBreakdown: [],
            hashtagBreakdown: []
          })
        })
        .finally(() => {
          setPerformanceLoading(false)
        })
    }
  }, [isOpen, asset, loadAsset, loadAssetPerformance, showError])

  // Load activations when drawer opens (so count is available in tab)
  useEffect(() => {
    if (isOpen && asset && activations.length === 0 && !activationsLoading) {
      setActivationsLoading(true)
      assetService.getAssetActivations(asset.id, asset.customer_id)
        .then((data) => setActivations(data))
        .catch(() => setActivations([]))
        .finally(() => setActivationsLoading(false))
    }
  }, [isOpen, asset, activations.length, activationsLoading])

  // Load posts when drawer opens (so count is available in tab)
  useEffect(() => {
    if (isOpen && asset && posts.length === 0 && !postsLoading) {
      setPostsLoading(true)
      assetService.getAssetPosts(asset.id, asset.customer_id, 1, 100)
        .then((result) => setPosts(result.data))
        .catch(() => setPosts([]))
        .finally(() => setPostsLoading(false))
    }
  }, [isOpen, asset, posts.length, postsLoading])

  const displayAsset = selectedAsset || asset

  if (!displayAsset) return null

  const getStatusBadgeClasses = (status: string) => {
    switch (status) {
      case 'Active':
        return 'bg-green-100 text-green-800'
      case 'Inactive':
        return 'bg-gray-100 text-gray-800'
      case 'Pending':
        return 'bg-yellow-100 text-yellow-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  const handleActivationToggle = async (activationId: string) => {
    if (expandedActivationId === activationId) {
      // Collapse if already expanded
      setExpandedActivationId(null)
      return
    }

    // Expand this activation
    setExpandedActivationId(activationId)

    // Load performance data if not cached
    if (!activationPerformanceCache[activationId] && asset) {
      setActivationPerfLoading(activationId)
      try {
        const perfData = await assetService.getAssetActivationPerformance(
          asset.id,
          activationId,
          asset.customer_id
        )
        setActivationPerformanceCache(prev => ({
          ...prev,
          [activationId]: perfData
        }))
      } catch (error) {
        console.error('Failed to load activation performance:', error)
      } finally {
        setActivationPerfLoading(null)
      }
    }
  }

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 z-40"
            onClick={onClose}
          />

          {/* Drawer */}
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'tween', duration: 0.3 }}
            className="fixed right-0 top-0 h-full w-full max-w-2xl bg-white shadow-xl z-50 flex flex-col"
          >
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg">
                  <Package className="h-5 w-5 text-white" />
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">{displayAsset.name}</h2>
                  <p className="text-sm text-gray-500">Asset Insights</p>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                {displayAsset.status && (
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getStatusBadgeClasses(displayAsset.status)}`}>
                    {displayAsset.status}
                  </span>
                )}
                <button
                  onClick={onClose}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <X className="h-5 w-5 text-gray-400" />
                </button>
              </div>
            </div>

            {/* Tabs */}
            <div className="flex items-center justify-between border-b border-gray-200 px-4">
              <div className="flex">
                <button
                  onClick={() => setActiveTab('overview')}
                  className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === 'overview'
                      ? 'border-purple-600 text-purple-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  Overview
                </button>
                <button
                  onClick={() => setActiveTab('activations')}
                  className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === 'activations'
                      ? 'border-purple-600 text-purple-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  Activations ({activations.length})
                </button>
                <button
                  onClick={() => setActiveTab('posts')}
                  className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === 'posts'
                      ? 'border-purple-600 text-purple-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  Posts ({posts.length})
                </button>
              </div>
              {onEditClick && (
                <button
                  onClick={onEditClick}
                  className="flex items-center px-3 py-1.5 bg-gradient-to-r from-purple-600 to-purple-700 text-white text-xs font-medium rounded-lg hover:from-purple-700 hover:to-purple-800 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 transition-all duration-200 shadow-sm hover:shadow-md"
                >
                  <Edit className="h-3.5 w-3.5 mr-1.5" />
                  Edit Asset
                </button>
              )}
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto">
              {performanceLoading && activeTab === 'overview' && (
                <div className="flex items-center justify-center h-64">
                  <div className="flex items-center space-x-3">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-purple-600"></div>
                    <span className="text-xs text-gray-900">Loading performance data...</span>
                  </div>
                </div>
              )}

              {!performanceLoading && activeTab === 'overview' && (
                <div className="p-6 space-y-4">
                  {/* Total EMV Card */}
                  {performanceData && performanceData.totalEmv > 0 && (
                    <Card className="p-4 bg-gradient-to-br from-slate-50 to-gray-100 border-slate-200">
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-1.5">
                          Total Value Generated
                          <Tooltip
                            content="Total Earned Media Value from posts matching this asset's account, hashtag, and format criteria."
                            placement="top"
                            maxWidth={240}
                          >
                            <Info className="h-3.5 w-3.5 text-gray-400 hover:text-gray-600 cursor-help" />
                          </Tooltip>
                        </h3>
                        <TrendingUp className="h-4 w-4 text-gray-500" />
                      </div>
                      <div className="flex items-end justify-between">
                        <div>
                          <p className="text-2xl font-bold text-green-600">
                            {formatCurrency(performanceData.totalEmv)}
                          </p>
                          <p className="text-xs text-gray-500">
                            from {formatNumber(performanceData.totalPosts)} posts
                          </p>
                        </div>
                        {performanceData.totalPosts > 0 && (
                          <div className="text-right">
                            <p className="text-sm font-semibold text-gray-700">
                              {formatCurrency(performanceData.totalEmv / performanceData.totalPosts)}
                            </p>
                            <p className="text-xs text-gray-500">avg per post</p>
                          </div>
                        )}
                      </div>
                    </Card>
                  )}

                  {/* Performance Overview */}
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900 mb-3">Performance Overview</h3>
                    <div className="grid grid-cols-2 gap-3">
                      <Card className="px-3 py-2">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-xs font-medium text-gray-500 flex items-center gap-1">
                              Total Posts
                              <Tooltip
                                content="Posts that match this asset's account, hashtag, and format criteria."
                                placement="top"
                                maxWidth={200}
                              >
                                <Info className="h-3 w-3 text-gray-400 hover:text-gray-600 cursor-help" />
                              </Tooltip>
                            </p>
                            <p className="text-base font-bold text-gray-900">
                              {formatNumber(performanceData?.totalPosts || 0)}
                            </p>
                          </div>
                          <div className="p-1.5 bg-blue-100 rounded-md">
                            <BarChart3 className="h-3.5 w-3.5 text-blue-600" />
                          </div>
                        </div>
                      </Card>

                      <Card className="px-3 py-2">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-xs font-medium text-gray-500 flex items-center gap-1">
                              Total EMV
                              <Tooltip
                                content="Earned Media Value from all posts matching this asset. This asset receives full credit for each matched post."
                                placement="top"
                                maxWidth={220}
                              >
                                <Info className="h-3 w-3 text-gray-400 hover:text-gray-600 cursor-help" />
                              </Tooltip>
                            </p>
                            <p className="text-base font-bold text-green-600">
                              {formatCurrency(performanceData?.totalEmv || 0)}
                            </p>
                          </div>
                          <div className="p-1.5 bg-green-100 rounded-md">
                            <DollarSign className="h-3.5 w-3.5 text-green-600" />
                          </div>
                        </div>
                      </Card>

                      <Card className="px-3 py-2">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-xs font-medium text-gray-500 flex items-center gap-1">
                              Total Views
                              <Tooltip
                                content="Combined view count from all posts matching this asset."
                                placement="top"
                                maxWidth={200}
                              >
                                <Info className="h-3 w-3 text-gray-400 hover:text-gray-600 cursor-help" />
                              </Tooltip>
                            </p>
                            <p className="text-base font-bold text-gray-900">
                              {formatNumber(performanceData?.totalViews || 0)}
                            </p>
                          </div>
                          <div className="p-1.5 bg-purple-100 rounded-md">
                            <Eye className="h-3.5 w-3.5 text-purple-600" />
                          </div>
                        </div>
                      </Card>

                      <Card className="px-3 py-2">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-xs font-medium text-gray-500 flex items-center gap-1">
                              Total Likes
                              <Tooltip
                                content="Combined like count from all posts matching this asset."
                                placement="top"
                                maxWidth={180}
                              >
                                <Info className="h-3 w-3 text-gray-400 hover:text-gray-600 cursor-help" />
                              </Tooltip>
                            </p>
                            <p className="text-base font-bold text-gray-900">
                              {formatNumber(performanceData?.totalLikes || 0)}
                            </p>
                          </div>
                          <div className="p-1.5 bg-red-100 rounded-md">
                            <Heart className="h-3.5 w-3.5 text-red-600" />
                          </div>
                        </div>
                      </Card>

                      <Card className="px-3 py-2">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-xs font-medium text-gray-500 flex items-center gap-1">
                              Comments
                              <Tooltip
                                content="Combined comment count from all posts matching this asset."
                                placement="top"
                                maxWidth={180}
                              >
                                <Info className="h-3 w-3 text-gray-400 hover:text-gray-600 cursor-help" />
                              </Tooltip>
                            </p>
                            <p className="text-base font-bold text-gray-900">
                              {formatNumber(performanceData?.totalComments || 0)}
                            </p>
                          </div>
                          <div className="p-1.5 bg-blue-100 rounded-md">
                            <MessageCircle className="h-3.5 w-3.5 text-blue-600" />
                          </div>
                        </div>
                      </Card>

                      <Card className="px-3 py-2">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-xs font-medium text-gray-500 flex items-center gap-1">
                              Shares
                              <Tooltip
                                content="Combined share count from all posts matching this asset."
                                placement="top"
                                maxWidth={180}
                              >
                                <Info className="h-3 w-3 text-gray-400 hover:text-gray-600 cursor-help" />
                              </Tooltip>
                            </p>
                            <p className="text-base font-bold text-gray-900">
                              {formatNumber(performanceData?.totalShares || 0)}
                            </p>
                          </div>
                          <div className="p-1.5 bg-green-100 rounded-md">
                            <Share2 className="h-3.5 w-3.5 text-green-600" />
                          </div>
                        </div>
                      </Card>
                    </div>
                  </div>

                  {/* EMV Contribution Breakdown */}
                  {performanceData && (
                    <Card className="p-4">
                      <h3 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-1.5">
                        EMV Contribution Breakdown
                        <Tooltip
                          content="Shows how this asset's EMV is distributed across accounts, tags, and formats."
                          placement="top"
                          maxWidth={220}
                        >
                          <Info className="h-3.5 w-3.5 text-gray-400 hover:text-gray-600 cursor-help" />
                        </Tooltip>
                      </h3>
                      <div className="space-y-4">
                        {/* By Account */}
                        {performanceData.accountBreakdown && performanceData.accountBreakdown.length > 0 ? (
                            <div>
                              <div className="flex items-center justify-between mb-2">
                                <p className="text-xs font-medium text-gray-600 flex items-center space-x-1">
                                  <Users className="h-3 w-3" />
                                  <span>By Account</span>
                                </p>
                                <span className="text-xs font-semibold text-green-600">
                                  {formatCurrency(performanceData.accountBreakdown.reduce((sum: number, a: any) => sum + a.emv, 0))}
                                </span>
                              </div>
                              <div className="space-y-2">
                                {(expandedSections.accounts ? performanceData.accountBreakdown : performanceData.accountBreakdown.slice(0, 4)).map((account: any, index: number) => {
                                  const colors = ['#10B981', '#34D399', '#6EE7B7', '#A7F3D0']
                                  const maxEmv = Math.max(...performanceData.accountBreakdown.map((a: any) => a.emv), 1)
                                  const pct = (account.emv / maxEmv) * 100
                                  const PlatformIcon = getPlatformIcon(account.platformId)
                                  return (
                                    <div key={account.accountId}>
                                      <div className="flex justify-between text-xs mb-0.5">
                                        <span className="text-gray-700 truncate flex items-center space-x-1.5" style={{ maxWidth: '60%' }}>
                                          <span className={getPlatformColor(account.platformId)}>
                                            <PlatformIcon className="h-3 w-3" />
                                          </span>
                                          <span>@{account.username}</span>
                                        </span>
                                        <span className="text-gray-500">{formatCurrency(account.emv)}</span>
                                      </div>
                                      <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                                        <div
                                          className="h-full rounded-full"
                                          style={{ width: `${pct}%`, backgroundColor: colors[index % colors.length] }}
                                        />
                                      </div>
                                    </div>
                                  )
                                })}
                                {performanceData.accountBreakdown.length > 4 && (
                                  <button
                                    onClick={() => setExpandedSections(prev => ({ ...prev, accounts: !prev.accounts }))}
                                    className="text-xs text-blue-600 hover:text-blue-700 font-medium mt-1"
                                  >
                                    {expandedSections.accounts ? 'Show less' : `+${performanceData.accountBreakdown.length - 4} more accounts`}
                                  </button>
                                )}
                              </div>
                            </div>
                          ) : (
                            <div>
                              <p className="text-xs font-medium text-gray-600 mb-2 flex items-center space-x-1">
                                <Users className="h-3 w-3" />
                                <span>By Account</span>
                              </p>
                              <p className="text-xs text-gray-400 italic">No account data available</p>
                            </div>
                          )}

                        {/* By Attribution Tag */}
                        {performanceData.hashtagBreakdown && performanceData.hashtagBreakdown.length > 0 ? (
                            <div>
                              <div className="flex items-center justify-between mb-2">
                                <p className="text-xs font-medium text-gray-600 flex items-center space-x-1">
                                  <Hash className="h-3 w-3" />
                                  <span>By Attribution Tag</span>
                                </p>
                                <span className="text-xs font-semibold text-green-600">
                                  {formatCurrency(performanceData.hashtagBreakdown.reduce((sum: number, h: any) => sum + h.emv, 0))}
                                </span>
                              </div>
                              <div className="space-y-2">
                                {(expandedSections.hashtags ? performanceData.hashtagBreakdown : performanceData.hashtagBreakdown.slice(0, 4)).map((hashtag: any, index: number) => {
                                  const colors = ['#F59E0B', '#FBBF24', '#FCD34D', '#FDE68A']
                                  const maxEmv = Math.max(...performanceData.hashtagBreakdown.map((h: any) => h.emv), 1)
                                  const pct = (hashtag.emv / maxEmv) * 100
                                  return (
                                    <div key={hashtag.hashtagId}>
                                      <div className="flex justify-between text-xs mb-0.5">
                                        <span className="text-gray-700 truncate" style={{ maxWidth: '60%' }}>
                                          {hashtag.hashtagText.startsWith('@') || hashtag.hashtagText.startsWith('#') ? hashtag.hashtagText : `#${hashtag.hashtagText}`}
                                        </span>
                                        <span className="text-gray-500">{formatCurrency(hashtag.emv)}</span>
                                      </div>
                                      <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                                        <div
                                          className="h-full rounded-full"
                                          style={{ width: `${pct}%`, backgroundColor: colors[index % colors.length] }}
                                        />
                                      </div>
                                    </div>
                                  )
                                })}
                                {performanceData.hashtagBreakdown.length > 4 && (
                                  <button
                                    onClick={() => setExpandedSections(prev => ({ ...prev, hashtags: !prev.hashtags }))}
                                    className="text-xs text-blue-600 hover:text-blue-700 font-medium mt-1"
                                  >
                                    {expandedSections.hashtags ? 'Show less' : `+${performanceData.hashtagBreakdown.length - 4} more tags`}
                                  </button>
                                )}
                              </div>
                            </div>
                          ) : (
                            <div>
                              <p className="text-xs font-medium text-gray-600 mb-2 flex items-center space-x-1">
                                <Hash className="h-3 w-3" />
                                <span>By Attribution Tag</span>
                              </p>
                              <p className="text-xs text-gray-400 italic">No tag data available</p>
                            </div>
                          )}

                        {/* By Format */}
                        {performanceData.categoryBreakdown && performanceData.categoryBreakdown.length > 0 ? (
                            <div>
                              <div className="flex items-center justify-between mb-2">
                                <p className="text-xs font-medium text-gray-600 flex items-center space-x-1">
                                  <FileText className="h-3 w-3" />
                                  <span>By Format</span>
                                </p>
                                <span className="text-xs font-semibold text-green-600">
                                  {formatCurrency(performanceData.categoryBreakdown.reduce((sum: number, c: any) => sum + c.emv, 0))}
                                </span>
                              </div>
                              <div className="space-y-2">
                                {(expandedSections.formats ? performanceData.categoryBreakdown : performanceData.categoryBreakdown.slice(0, 4)).map((category: any, index: number) => {
                                  const colors = ['#8B5CF6', '#A78BFA', '#C4B5FD', '#DDD6FE']
                                  const maxEmv = Math.max(...performanceData.categoryBreakdown.map((c: any) => c.emv), 1)
                                  const pct = (category.emv / maxEmv) * 100
                                  return (
                                    <div key={category.categoryName}>
                                      <div className="flex justify-between text-xs mb-0.5">
                                        <span className="text-gray-700 truncate" style={{ maxWidth: '60%' }}>
                                          {category.categoryName}
                                        </span>
                                        <span className="text-gray-500">{formatCurrency(category.emv)}</span>
                                      </div>
                                      <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                                        <div
                                          className="h-full rounded-full"
                                          style={{ width: `${pct}%`, backgroundColor: colors[index % colors.length] }}
                                        />
                                      </div>
                                    </div>
                                  )
                                })}
                                {performanceData.categoryBreakdown.length > 4 && (
                                  <button
                                    onClick={() => setExpandedSections(prev => ({ ...prev, formats: !prev.formats }))}
                                    className="text-xs text-blue-600 hover:text-blue-800 mt-1 cursor-pointer"
                                  >
                                    {expandedSections.formats ? 'Show less' : `+${performanceData.categoryBreakdown.length - 4} more formats`}
                                  </button>
                                )}
                              </div>
                            </div>
                          ) : (
                            <div>
                              <p className="text-xs font-medium text-gray-600 mb-2 flex items-center space-x-1">
                                <FileText className="h-3 w-3" />
                                <span>By Format</span>
                              </p>
                              <p className="text-xs text-gray-400 italic">No format data available</p>
                            </div>
                          )}
                        </div>
                      </Card>
                  )}

                  {/* Post Formats */}
                  {displayAsset.category_names && displayAsset.category_names.length > 0 && (
                    <Card className="px-3 py-2">
                      <h3 className="text-xs font-semibold text-gray-900 mb-1">Post Formats</h3>
                      <div className="flex flex-wrap gap-1.5">
                        {displayAsset.category_names.map((name, idx) => (
                          <span
                            key={idx}
                            className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700"
                          >
                            {name}
                          </span>
                        ))}
                      </div>
                    </Card>
                  )}
                </div>
              )}

              {activeTab === 'activations' && (
                <div className="p-6">
                  {/* Status Filter */}
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-xs text-gray-500">
                      {filteredActivations.length} of {activations.length} activations
                    </span>
                    <div className="relative">
                      <Filter className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
                      <select
                        value={activationStatusFilter}
                        onChange={(e) => setActivationStatusFilter(e.target.value)}
                        className="pl-8 pr-8 py-1.5 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent appearance-none bg-white cursor-pointer"
                      >
                        <option value="all">All Statuses</option>
                        <option value="Active">Active ({activations.filter(a => a.status === 'Active').length})</option>
                        <option value="Pending">Pending ({activations.filter(a => a.status === 'Pending').length})</option>
                        <option value="Suspended">Suspended ({activations.filter(a => a.status === 'Suspended').length})</option>
                        <option value="Completed">Completed ({activations.filter(a => a.status === 'Completed').length})</option>
                      </select>
                    </div>
                  </div>

                  {activationsLoading ? (
                    <div className="flex items-center justify-center h-64">
                      <div className="flex items-center space-x-3">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-purple-600"></div>
                        <span className="text-xs text-gray-900">Loading activations...</span>
                      </div>
                    </div>
                  ) : filteredActivations.length > 0 ? (
                    <div className="space-y-4">
                      {filteredActivations.map((activation, index) => {
                        const startDate = activation.start_date ? formatDate(activation.start_date) : null
                        const endDate = activation.end_date ? formatDate(activation.end_date) : null

                        const isExpanded = expandedActivationId === activation.id
                        const perfData = activationPerformanceCache[activation.id]
                        const isLoadingPerf = activationPerfLoading === activation.id

                        return (
                          <motion.div
                            key={activation.id}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: index * 0.05 }}
                          >
                            <Card
                              className="overflow-hidden hover:shadow-md transition-shadow cursor-pointer"
                              onClick={() => handleActivationToggle(activation.id)}
                            >
                              {/* Header row */}
                              <div className="p-4 flex items-start space-x-4">
                                <div className="flex-shrink-0">
                                  {activation.partner_logo_url && asset ? (
                                    <img
                                      src={getPartnerLogoUrl(activation.partner_id, asset.customer_id)}
                                      alt={activation.partner_name || 'Partner'}
                                      className="w-10 h-10 object-contain rounded border border-gray-100"
                                      onError={(e) => {
                                        e.currentTarget.style.display = 'none'
                                      }}
                                    />
                                  ) : (
                                    <div className="w-10 h-10 rounded bg-gray-100 flex items-center justify-center">
                                      <Building2 className="h-5 w-5 text-gray-400" />
                                    </div>
                                  )}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center justify-between mb-1">
                                    <h4 className="text-sm font-medium text-gray-900 truncate">
                                      {activation.name}
                                    </h4>
                                    <div className="flex items-center space-x-2">
                                      <span className={`flex-shrink-0 px-2 py-0.5 rounded-full text-xs font-medium ${
                                        activation.status === 'Active'
                                          ? 'bg-green-100 text-green-700'
                                          : activation.status === 'Completed'
                                          ? 'bg-blue-100 text-blue-700'
                                          : activation.status === 'Pending'
                                          ? 'bg-yellow-100 text-yellow-700'
                                          : 'bg-gray-100 text-gray-600'
                                      }`}>
                                        {activation.status}
                                      </span>
                                      <ChevronDown
                                        className={`h-4 w-4 text-gray-400 transition-transform duration-200 ${
                                          isExpanded ? 'rotate-180' : ''
                                        }`}
                                      />
                                    </div>
                                  </div>
                                  {activation.partner_name && (
                                    <p className="text-xs text-gray-500 mb-2">{activation.partner_name}</p>
                                  )}
                                  <div className="flex items-center space-x-4 text-xs text-gray-500">
                                    {(startDate || endDate) && (
                                      <span className="flex items-center space-x-1">
                                        <Calendar className="h-3 w-3" />
                                        <span>{startDate} - {endDate}</span>
                                      </span>
                                    )}
                                    {activation.deal_value && (
                                      <span className="flex items-center space-x-1 text-green-600 font-medium">
                                        <DollarSign className="h-3 w-3" />
                                        <span>{formatCurrency(activation.deal_value)}</span>
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </div>

                              {/* Expanded content */}
                              <AnimatePresence>
                                {isExpanded && (
                                  <motion.div
                                    initial={{ height: 0, opacity: 0 }}
                                    animate={{ height: 'auto', opacity: 1 }}
                                    exit={{ height: 0, opacity: 0 }}
                                    transition={{ duration: 0.2 }}
                                    className="overflow-hidden"
                                  >
                                    <div className="px-4 pb-4 pt-2 border-t border-gray-100">
                                      {isLoadingPerf ? (
                                        <div className="flex items-center justify-center py-6">
                                          <div className="flex items-center space-x-2">
                                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-purple-600"></div>
                                            <span className="text-xs text-gray-500">Loading performance...</span>
                                          </div>
                                        </div>
                                      ) : perfData ? (
                                        perfData.totalPosts === 0 ? (
                                          <div className="text-center py-4">
                                            <p className="text-xs text-gray-500">No performance data for this activation period</p>
                                          </div>
                                        ) : (
                                          <div className="space-y-4">
                                            {/* Metrics Grid - 3 columns */}
                                            <div className="grid grid-cols-3 gap-2">
                                              <div className="px-2 py-1.5 bg-blue-50 rounded">
                                                <p className="text-[10px] text-gray-500">Posts</p>
                                                <p className="text-sm font-bold text-gray-900">{formatNumber(perfData.totalPosts)}</p>
                                              </div>
                                              <div className="px-2 py-1.5 bg-green-50 rounded">
                                                <p className="text-[10px] text-gray-500">EMV</p>
                                                <p className="text-sm font-bold text-green-600">{formatCurrency(perfData.totalEmv)}</p>
                                              </div>
                                              <div className="px-2 py-1.5 bg-purple-50 rounded">
                                                <p className="text-[10px] text-gray-500">Views</p>
                                                <p className="text-sm font-bold text-gray-900">{formatNumber(perfData.totalViews)}</p>
                                              </div>
                                              <div className="px-2 py-1.5 bg-red-50 rounded">
                                                <p className="text-[10px] text-gray-500">Likes</p>
                                                <p className="text-sm font-bold text-gray-900">{formatNumber(perfData.totalLikes)}</p>
                                              </div>
                                              <div className="px-2 py-1.5 bg-blue-50 rounded">
                                                <p className="text-[10px] text-gray-500">Comments</p>
                                                <p className="text-sm font-bold text-gray-900">{formatNumber(perfData.totalComments)}</p>
                                              </div>
                                              <div className="px-2 py-1.5 bg-green-50 rounded">
                                                <p className="text-[10px] text-gray-500">Shares</p>
                                                <p className="text-sm font-bold text-gray-900">{formatNumber(perfData.totalShares)}</p>
                                              </div>
                                            </div>

                                            {/* EMV Breakdowns - compact version */}
                                            <div className="space-y-3 pt-2 border-t border-gray-100 mt-3">
                                              <p className="text-[10px] font-semibold text-gray-700 uppercase tracking-wider">EMV Contribution</p>

                                              {/* By Account - top 3 */}
                                              {perfData.accountBreakdown && perfData.accountBreakdown.length > 0 ? (
                                                <div>
                                                  <div className="flex items-center justify-between mb-1.5">
                                                    <p className="text-[10px] font-medium text-gray-600 flex items-center space-x-1">
                                                      <Users className="h-2.5 w-2.5" />
                                                      <span>By Account</span>
                                                    </p>
                                                    <span className="text-[10px] font-semibold text-green-600">
                                                      {formatCurrency(perfData.accountBreakdown.reduce((sum: number, a: any) => sum + a.emv, 0))}
                                                    </span>
                                                  </div>
                                                  <div className="space-y-1">
                                                    {perfData.accountBreakdown.slice(0, 3).map((account: any, idx: number) => {
                                                      const colors = ['#10B981', '#34D399', '#6EE7B7']
                                                      const maxEmv = Math.max(...perfData.accountBreakdown.map((a: any) => a.emv), 1)
                                                      const pct = (account.emv / maxEmv) * 100
                                                      const PlatformIcon = getPlatformIcon(account.platformId)
                                                      return (
                                                        <div key={account.accountId}>
                                                          <div className="flex justify-between text-[10px] mb-0.5">
                                                            <span className="text-gray-700 truncate flex items-center space-x-1" style={{ maxWidth: '60%' }}>
                                                              <span className={getPlatformColor(account.platformId)}>
                                                                <PlatformIcon className="h-2.5 w-2.5" />
                                                              </span>
                                                              <span>@{account.username}</span>
                                                            </span>
                                                            <span className="text-gray-500">{formatCurrency(account.emv)}</span>
                                                          </div>
                                                          <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                                                            <div
                                                              className="h-full rounded-full"
                                                              style={{ width: `${pct}%`, backgroundColor: colors[idx % colors.length] }}
                                                            />
                                                          </div>
                                                        </div>
                                                      )
                                                    })}
                                                    {perfData.accountBreakdown.length > 3 && (
                                                      <p className="text-[10px] text-gray-400">
                                                        +{perfData.accountBreakdown.length - 3} more
                                                      </p>
                                                    )}
                                                  </div>
                                                </div>
                                              ) : (
                                                <div>
                                                  <p className="text-[10px] font-medium text-gray-600 mb-1 flex items-center space-x-1">
                                                    <Users className="h-2.5 w-2.5" />
                                                    <span>By Account</span>
                                                  </p>
                                                  <p className="text-[10px] text-gray-400 italic">No account data available</p>
                                                </div>
                                              )}

                                              {/* By Attribution Tag - top 3 */}
                                              {perfData.hashtagBreakdown && perfData.hashtagBreakdown.length > 0 ? (
                                                <div>
                                                  <div className="flex items-center justify-between mb-1.5">
                                                    <p className="text-[10px] font-medium text-gray-600 flex items-center space-x-1">
                                                      <Hash className="h-2.5 w-2.5" />
                                                      <span>By Attribution Tag</span>
                                                    </p>
                                                    <span className="text-[10px] font-semibold text-green-600">
                                                      {formatCurrency(perfData.hashtagBreakdown.reduce((sum: number, h: any) => sum + h.emv, 0))}
                                                    </span>
                                                  </div>
                                                  <div className="space-y-1">
                                                    {perfData.hashtagBreakdown.slice(0, 3).map((hashtag: any, idx: number) => {
                                                      const colors = ['#F59E0B', '#FBBF24', '#FCD34D']
                                                      const maxEmv = Math.max(...perfData.hashtagBreakdown.map((h: any) => h.emv), 1)
                                                      const pct = (hashtag.emv / maxEmv) * 100
                                                      return (
                                                        <div key={hashtag.hashtagId}>
                                                          <div className="flex justify-between text-[10px] mb-0.5">
                                                            <span className="text-gray-700 truncate" style={{ maxWidth: '60%' }}>
                                                              {hashtag.hashtagText.startsWith('@') || hashtag.hashtagText.startsWith('#') ? hashtag.hashtagText : `#${hashtag.hashtagText}`}
                                                            </span>
                                                            <span className="text-gray-500">{formatCurrency(hashtag.emv)}</span>
                                                          </div>
                                                          <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                                                            <div
                                                              className="h-full rounded-full"
                                                              style={{ width: `${pct}%`, backgroundColor: colors[idx % colors.length] }}
                                                            />
                                                          </div>
                                                        </div>
                                                      )
                                                    })}
                                                    {perfData.hashtagBreakdown.length > 3 && (
                                                      <p className="text-[10px] text-gray-400">
                                                        +{perfData.hashtagBreakdown.length - 3} more
                                                      </p>
                                                    )}
                                                  </div>
                                                </div>
                                              ) : (
                                                <div>
                                                  <p className="text-[10px] font-medium text-gray-600 mb-1 flex items-center space-x-1">
                                                    <Hash className="h-2.5 w-2.5" />
                                                    <span>By Attribution Tag</span>
                                                  </p>
                                                  <p className="text-[10px] text-gray-400 italic">No tag data available</p>
                                                </div>
                                              )}

                                              {/* By Format - top 3 */}
                                              {perfData.categoryBreakdown && perfData.categoryBreakdown.length > 0 ? (
                                                <div>
                                                  <div className="flex items-center justify-between mb-1.5">
                                                    <p className="text-[10px] font-medium text-gray-600 flex items-center space-x-1">
                                                      <FileText className="h-2.5 w-2.5" />
                                                      <span>By Format</span>
                                                    </p>
                                                    <span className="text-[10px] font-semibold text-green-600">
                                                      {formatCurrency(perfData.categoryBreakdown.reduce((sum: number, c: any) => sum + c.emv, 0))}
                                                    </span>
                                                  </div>
                                                  <div className="space-y-1">
                                                    {perfData.categoryBreakdown.slice(0, 3).map((category: any, idx: number) => {
                                                      const colors = ['#8B5CF6', '#A78BFA', '#C4B5FD']
                                                      const maxEmv = Math.max(...perfData.categoryBreakdown.map((c: any) => c.emv), 1)
                                                      const pct = (category.emv / maxEmv) * 100
                                                      return (
                                                        <div key={category.categoryName}>
                                                          <div className="flex justify-between text-[10px] mb-0.5">
                                                            <span className="text-gray-700 truncate" style={{ maxWidth: '60%' }}>
                                                              {category.categoryName}
                                                            </span>
                                                            <span className="text-gray-500">{formatCurrency(category.emv)}</span>
                                                          </div>
                                                          <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                                                            <div
                                                              className="h-full rounded-full"
                                                              style={{ width: `${pct}%`, backgroundColor: colors[idx % colors.length] }}
                                                            />
                                                          </div>
                                                        </div>
                                                      )
                                                    })}
                                                    {perfData.categoryBreakdown.length > 3 && (
                                                      <p className="text-[10px] text-gray-400">
                                                        +{perfData.categoryBreakdown.length - 3} more
                                                      </p>
                                                    )}
                                                  </div>
                                                </div>
                                              ) : (
                                                <div>
                                                  <p className="text-[10px] font-medium text-gray-600 mb-1 flex items-center space-x-1">
                                                    <FileText className="h-2.5 w-2.5" />
                                                    <span>By Format</span>
                                                  </p>
                                                  <p className="text-[10px] text-gray-400 italic">No format data available</p>
                                                </div>
                                              )}
                                            </div>
                                          </div>
                                        )
                                      ) : (
                                        <div className="text-center py-4">
                                          <p className="text-xs text-red-500">Failed to load performance data</p>
                                        </div>
                                      )}
                                    </div>
                                  </motion.div>
                                )}
                              </AnimatePresence>
                            </Card>
                          </motion.div>
                        )
                      })}
                    </div>
                  ) : activations.length === 0 ? (
                    <div className="text-center py-12">
                      <Zap className="mx-auto h-10 w-10 text-gray-400" />
                      <h3 className="mt-2 text-sm font-medium text-gray-900">No activations</h3>
                      <p className="mt-1 text-xs text-gray-500">
                        This asset is not linked to any activations yet.
                      </p>
                    </div>
                  ) : (
                    <div className="text-center py-12">
                      <Filter className="mx-auto h-10 w-10 text-gray-400" />
                      <h3 className="mt-2 text-sm font-medium text-gray-900">No matching activations</h3>
                      <p className="mt-1 text-xs text-gray-500">
                        No activations match the selected filter.
                      </p>
                      <button
                        onClick={() => setActivationStatusFilter('all')}
                        className="mt-3 text-xs text-purple-600 hover:text-purple-700 font-medium"
                      >
                        Clear filter
                      </button>
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'posts' && (
                <div className="p-6">
                  {/* Search bar */}
                  <div className="flex items-center justify-between mb-4">
                    <div className="relative w-1/3">
                      <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
                      <input
                        type="text"
                        value={postsSearch}
                        onChange={(e) => setPostsSearch(e.target.value)}
                        placeholder="Search posts..."
                        className="w-full pl-8 pr-8 py-1.5 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      />
                      {postsSearch && (
                        <button
                          onClick={() => setPostsSearch('')}
                          className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        >
                          <X className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </div>
                    {postsSearch && (
                      <p className="text-xs text-gray-500">
                        {filteredPosts.length} of {posts.length} posts
                      </p>
                    )}
                  </div>

                  {postsLoading ? (
                    <div className="flex items-center justify-center h-64">
                      <div className="flex items-center space-x-3">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-purple-600"></div>
                        <span className="text-xs text-gray-900">Loading posts...</span>
                      </div>
                    </div>
                  ) : filteredPosts.length > 0 ? (
                    <div className="space-y-4">
                      {filteredPosts.map((post, index) => {
                        const PlatformIcon = getPlatformIcon(post.platform_id)

                        return (
                          <motion.div
                            key={`${post.partition_key}-${post.post_id}`}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: index * 0.05 }}
                          >
                            <Card className="p-4 hover:shadow-md transition-shadow">
                              <div className="flex items-start space-x-4">
                                <div className="flex-shrink-0">
                                  <div className={`p-2 rounded-lg bg-gray-100 ${getPlatformColor(post.platform_id)}`}>
                                    <PlatformIcon className="h-5 w-5" />
                                  </div>
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center justify-between mb-2">
                                    <div className="flex items-center space-x-2">
                                      {post.username && (
                                        <p className="text-xs font-medium text-gray-900">
                                          @{post.username}
                                        </p>
                                      )}
                                    </div>
                                    <div className="flex items-center space-x-2">
                                      <span className="text-sm font-bold text-green-600">
                                        {formatCurrency(post.emv)}
                                      </span>
                                      {post.post_url && (
                                        <a
                                          href={post.post_url}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
                                          onClick={(e) => e.stopPropagation()}
                                        >
                                          <ExternalLink className="h-4 w-4" />
                                        </a>
                                      )}
                                    </div>
                                  </div>
                                  {post.caption && (
                                    <p className="text-xs text-gray-600 mb-3">
                                      {truncateCaption(post.caption)}
                                    </p>
                                  )}
                                  <div className="flex items-center justify-between text-xs text-gray-500">
                                    <div className="flex items-center space-x-4">
                                      <div className="flex items-center space-x-1">
                                        <Eye className="h-3 w-3" />
                                        <span>{formatNumber(post.views)}</span>
                                      </div>
                                      <div className="flex items-center space-x-1">
                                        <Heart className="h-3 w-3" />
                                        <span>{formatNumber(post.likes)}</span>
                                      </div>
                                      <div className="flex items-center space-x-1">
                                        <MessageCircle className="h-3 w-3" />
                                        <span>{formatNumber(post.comments)}</span>
                                      </div>
                                      <div className="flex items-center space-x-1">
                                        <Share2 className="h-3 w-3" />
                                        <span>{formatNumber(post.shares)}</span>
                                      </div>
                                    </div>
                                    <span>{formatDate(post.posted_at)}</span>
                                  </div>
                                </div>
                              </div>
                            </Card>
                          </motion.div>
                        )
                      })}
                    </div>
                  ) : posts.length === 0 ? (
                    <div className="text-center py-12">
                      <BarChart3 className="mx-auto h-10 w-10 text-gray-400" />
                      <h3 className="mt-2 text-sm font-medium text-gray-900">No posts found</h3>
                      <p className="mt-1 text-xs text-gray-500">
                        No posts have been attributed to this asset yet.
                      </p>
                    </div>
                  ) : (
                    <div className="text-center py-12">
                      <Search className="mx-auto h-10 w-10 text-gray-400" />
                      <h3 className="mt-2 text-sm font-medium text-gray-900">No matching posts</h3>
                      <p className="mt-1 text-xs text-gray-500">
                        No posts match your search criteria.
                      </p>
                      <button
                        onClick={() => setPostsSearch('')}
                        className="mt-3 text-xs text-purple-600 hover:text-purple-700 font-medium"
                      >
                        Clear search
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  )
}

export default AssetDetailDrawer
